import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import { Link, useLocation } from "react-router-dom";

import cls from "../../Lounge/Chat/ChatSingle/ChatSingle.module.scss";

import i18n from "@/i18n";
import { MEMBER_PROGRESS, PATHS } from "@/app/constants";
import admin from "@/assets/UI/admin.webp";
import homeIcon from "@/assets/UI/home.webp";
import api from "@/utils/axios";
import { connectSocket, getSocket } from "@/utils/socket";
import { useAuth } from "@/app/auth-context-state";
import VideoPlayer from "@/components/video/VideoPlayer/VideoPlayer";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];

const safeStr = (value) => (value == null ? "" : String(value));
const hasText = (value) => safeStr(value).trim().length > 0;

const extFromFile = (file) => {
  const name = safeStr(file?.name);
  const dot = name.lastIndexOf(".");
  if (dot === -1) return "";
  return name.slice(dot + 1).toLowerCase();
};

const toNumberOrNull = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const normalizeVariants = (variants) => {
  if (!Array.isArray(variants)) return undefined;
  const normalized = variants
    .map((variant) => {
      if (!variant) return null;
      const key = safeStr(variant.key).trim();
      if (!key) return null;
      return {
        key,
        type: safeStr(variant.type).trim() || null,
        w: toNumberOrNull(variant.w),
        h: toNumberOrNull(variant.h),
      };
    })
    .filter(Boolean);
  return normalized.length ? normalized : undefined;
};

const buildImageAttachment = (_file, presign) => {
  if (!presign?.processedKey) return null;
  return {
    type: "image",
    key: presign.processedKey,
    variants: normalizeVariants(presign.variants),
    w: toNumberOrNull(presign.width),
    h: toNumberOrNull(presign.height),
  };
};

const buildVideoAttachment = (_file, presign) => {
  if (!presign?.processedKey) return null;
  return {
    type: "video",
    videoId: safeStr(presign.videoId).trim() || null,
    key: presign.processedKey,
    variants: normalizeVariants(presign.variants),
    w: toNumberOrNull(presign.width),
    h: toNumberOrNull(presign.height),
    durationSec: toNumberOrNull(presign.durationSec),
  };
};

const buildImagePreview = (att) => {
  if (!att) return null;
  const variants = Array.isArray(att.variants) ? att.variants : [];
  const variantUrl =
    variants.find((variant) => safeStr(variant.url).trim())?.url || "";
  return safeStr(variantUrl || att.url || "").trim();
};

const buildVideoMeta = (att) => {
  if (!att) return null;
  return {
    posterUrl: safeStr(att.posterUrl || "").trim(),
    hlsUrl: safeStr(att.url || "").trim(),
    videoId: safeStr(att.videoId || "").trim(),
  };
};

async function sniffVideoMeta(file) {
  return new Promise((resolve) => {
    try {
      const el = document.createElement("video");
      el.preload = "metadata";
      el.muted = true;
      el.playsInline = true;

      el.onloadedmetadata = () => {
        URL.revokeObjectURL(el.src);
        const width = el.videoWidth || 0;
        const height = el.videoHeight || 0;
        const orientation =
          width && height && width < height ? "portrait" : "landscape";
        resolve({ width, height, orientation });
      };

      el.onerror = () => {
        try {
          URL.revokeObjectURL(el.src || "");
        } catch {
          // Intentionally ignored: this best-effort cleanup should not block the UI.
        }
        resolve({ width: 0, height: 0, orientation: "landscape" });
      };

      el.src = URL.createObjectURL(file);
    } catch {
      resolve({ width: 0, height: 0, orientation: "landscape" });
    }
  });
}

const putToS3 = (uploadUrl, file, contentTypeOverride) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", uploadUrl);
    xhr.setRequestHeader(
      "Content-Type",
      contentTypeOverride || file?.type || "application/octet-stream",
    );
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) return resolve();
      reject(new Error(`Upload failed (${xhr.status})`));
    };
    xhr.onerror = () => reject(new Error("Upload failed (network error)"));
    xhr.send(file);
  });

const formatTime = (dateInput) => {
  if (!dateInput) return "";
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateLabel = (dateInput) => {
  if (!dateInput) return "";
  const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;
  if (Number.isNaN(date.getTime())) return "";

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const startOfDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
  );

  const diffDays = Math.round(
    (startOfToday - startOfDate) / (1000 * 60 * 60 * 24),
  );

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return startOfDate.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

function getMemberStatusPath(status) {
  switch (status) {
    case MEMBER_PROGRESS.LOCAL:
      return PATHS.MEMBER_LOCAL;
    case MEMBER_PROGRESS.AVATAR:
      return PATHS.MEMBER_AVATAR;
    case MEMBER_PROGRESS.GENDER:
      return PATHS.MEMBER_GENDER;
    case MEMBER_PROGRESS.RELATION:
      return PATHS.MEMBER_RELATION;
    case MEMBER_PROGRESS.SEXUAL_IDENTITY:
      return PATHS.SEXUAL_IDENTITY;
    case MEMBER_PROGRESS.SEXUAL_ROLE:
      return PATHS.SEXUAL_ROLE;
    case MEMBER_PROGRESS.ATTRACTION_GENDERS:
      return PATHS.ATTRACTION_GENDERS;
    case MEMBER_PROGRESS.CONNECTION_TYPES:
      return PATHS.CONNECTION_TYPES;
    case MEMBER_PROGRESS.INTIMACY_TYPES:
      return PATHS.INTIMACY_TYPES;
    case MEMBER_PROGRESS.SEXUAL_FANTASIES:
      return PATHS.SEXUAL_FANTASIES;
    case MEMBER_PROGRESS.BODY_TYPE:
      return PATHS.BODY_TYPE;
    case MEMBER_PROGRESS.BODY_ATTRACT:
      return PATHS.BODY_ATTRACT;
    case MEMBER_PROGRESS.SEXUAL_ENVIRONMENT:
      return PATHS.SEXUAL_ENVIRONMENT;
    case MEMBER_PROGRESS.EVENT_ATTRACT:
      return PATHS.EVENT_ATTRACT;
    case MEMBER_PROGRESS.ALCOHOL_CANNABIS:
      return PATHS.ALCOHOL_CANNABIS;
    case MEMBER_PROGRESS.BE_MATCHED:
      return PATHS.BE_MATCHED;
    case MEMBER_PROGRESS.JOIN_HOST:
      return PATHS.JOIN_HOST;
    case MEMBER_PROGRESS.REAL_IMAGE:
      return PATHS.REAL_IMAGE;
    case MEMBER_PROGRESS.SOCIAL:
      return PATHS.SOCIAL;
    case MEMBER_PROGRESS.PAYMENT:
      return PATHS.PAYMENT;
    case MEMBER_PROGRESS.PENDING:
      return PATHS.PENDING;
    case MEMBER_PROGRESS.APPROVED:
      return PATHS.APPROVED;
    default:
      return PATHS.MEMBER_CREATE;
  }
}

const QuickIcon = ({ src, alt, ring = "pink", active = false }) => {
  const ringClass =
    ring === "pink"
      ? cls.ringPink
      : ring === "blue"
        ? cls.ringBlue
        : ring === "black"
          ? cls.ringBlack
          : "";

  return (
    <span
      className={`${cls.iconImgWrap} ${ringClass} ${
        active ? cls.iconActive : ""
      }`}
      aria-hidden="true"
    >
      <img className={cls.iconImg} src={src} alt={alt || ""} />
    </span>
  );
};

const PAGE_SIZE = 30;
const LOAD_MORE_THRESHOLD = 80;
const MEDIA_REFRESH_DELAY_MS = 3000;
const MEDIA_REFRESH_MAX_RETRIES = 10;

const Chat = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const currentUserId = user?.id || user?._id || null;
  const memberStatusPath = getMemberStatusPath(user?.members_status);

  const accessToken = (() => {
    try {
      return localStorage.getItem("cm_at");
    } catch {
      return null;
    }
  })();

  const [conversation, setConversation] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState("");

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [pendingAttachment, setPendingAttachment] = useState(null);

  const [isTyping, setIsTyping] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const typingTimeoutRef = useRef(null);
  const typingClearRef = useRef(null);
  const deliveredRef = useRef(new Set());
  const readRef = useRef(new Set());
  const mediaRefreshRef = useRef({ timeoutId: null, attempts: 0 });

  const fileInputRef = useRef(null);
  const messagePaneRef = useRef(null);
  const inputRef = useRef(null);

  const initialLoadRef = useRef(true);
  const isAtBottomRef = useRef(true);

  // ✅ Fix keyboard viewport / stuck zoom issues on mobile browsers (iOS especially)
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const apply = () => {
      document.documentElement.style.setProperty("--vvh", `${vv.height}px`);
    };

    apply();
    vv.addEventListener("resize", apply);
    vv.addEventListener("scroll", apply);
    return () => {
      vv.removeEventListener("resize", apply);
      vv.removeEventListener("scroll", apply);
    };
  }, []);

  const scrollToBottom = useCallback(() => {
    const pane = messagePaneRef.current;
    if (!pane) return;
    pane.scrollTop = pane.scrollHeight;
  }, []);

  const scrollToBottomSoon = useCallback(() => {
    requestAnimationFrame(() => scrollToBottom());
    window.setTimeout(() => scrollToBottom(), 60);
    window.setTimeout(() => scrollToBottom(), 180);
  }, [scrollToBottom]);

  const fetchConversationData = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const conversationRes = await api.get(
        "/v1/chat/conversations/resolve-admin",
      );
      const resolved = conversationRes.data?.conversation || null;
      const resolvedId = resolved?.id || resolved?._id || null;
      setConversation(resolved);
      setConversationId(resolvedId);

      if (!resolvedId) {
        setMessages([]);
        setHasMore(false);
        return;
      }

      const messageRes = await api.get(`/v1/chat/messages/${resolvedId}`, {
        params: { limit: PAGE_SIZE },
      });
      setMessages(messageRes.data?.messages || []);
      setHasMore(Boolean(messageRes.data?.hasMore));
      initialLoadRef.current = true;
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          "Unable to load this conversation. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversationData();
  }, [fetchConversationData]);

  useEffect(() => {
    if (!loading && initialLoadRef.current) {
      initialLoadRef.current = false;
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [loading, scrollToBottom]);

  useEffect(() => {
    return () => {
      if (pendingAttachment?.previewUrl) {
        URL.revokeObjectURL(pendingAttachment.previewUrl);
      }
    };
  }, [pendingAttachment]);

  const fallbackAdminUserId = useMemo(() => {
    const participants = conversation?.participants || [];
    const adminParticipant = participants.find(
      (participant) => !participant.memberId,
    );
    return adminParticipant?.userId || null;
  }, [conversation?.participants]);

  const fallbackMemberUserId = useMemo(() => {
    const participants = conversation?.participants || [];
    const memberParticipant = participants.find(
      (participant) => participant.memberId,
    );
    return memberParticipant?.userId || null;
  }, [conversation?.participants]);

  const effectiveUserId =
    currentUserId || fallbackMemberUserId || fallbackAdminUserId || null;

  const otherParticipants = useMemo(() => {
    const participants = conversation?.participants || [];
    const others = participants.filter(
      (participant) => participant.userId !== effectiveUserId,
    );
    return others.length ? others : participants;
  }, [conversation?.participants, effectiveUserId]);

  const headerName = useMemo(() => {
    const firstName = otherParticipants.find((participant) =>
      Boolean(participant.username),
    )?.username;
    return firstName || "Admin Support";
  }, [otherParticipants]);

  const headerAvatar = otherParticipants[0]?.avatarUrl || admin;

  const hasPendingMedia = useMemo(() => {
    return messages.some((message) => {
      const attachments = Array.isArray(message?.attachments)
        ? message.attachments
        : [];
      return attachments.some((att) => {
        if (!att?.type) return false;
        if (att.type === "image") {
          return !buildImagePreview(att);
        }
        if (att.type === "video") {
          const meta = buildVideoMeta(att);
          return !meta?.hlsUrl && !meta?.videoId;
        }
        return false;
      });
    });
  }, [messages]);

  useEffect(() => {
    if (!accessToken) return;
    const socket = connectSocket(accessToken);
    if (conversationId) {
      socket.emit("join:room", conversationId);
    }

    const handleTyping = (payload) => {
      if (!payload) return;
      if (payload.roomId && payload.roomId !== conversationId) return;
      if (String(payload.senderId) === String(effectiveUserId)) return;

      setIsTyping(Boolean(payload.isTyping));
      if (typingClearRef.current) clearTimeout(typingClearRef.current);

      if (payload.isTyping) {
        typingClearRef.current = setTimeout(() => {
          setIsTyping(false);
        }, 2500);
      }
    };

    const handleDelivered = (payload) => {
      if (!payload?.messageId) return;
      setMessages((prev) =>
        prev.map((msg) => {
          if (
            String(msg.id || msg._id) !== String(payload.messageId) ||
            String(msg.senderId) !== String(effectiveUserId)
          ) {
            return msg;
          }
          return {
            ...msg,
            status: payload.status || "delivered",
            deliveredAt: payload.deliveredAt || msg.deliveredAt,
          };
        }),
      );
    };

    const handleMessage = (payload) => {
      if (!payload) return;
      if (
        payload.conversationId &&
        String(payload.conversationId) !== String(conversationId)
      ) {
        return;
      }
      const payloadId = payload.id || payload._id;
      setMessages((prev) => {
        if (
          payloadId &&
          prev.some((msg) => String(msg.id || msg._id) === String(payloadId))
        ) {
          return prev;
        }
        return [...prev, payload];
      });
    };

    const handleRead = (payload) => {
      if (!payload?.messageId) return;
      setMessages((prev) =>
        prev.map((msg) => {
          if (
            String(msg.id || msg._id) !== String(payload.messageId) ||
            String(msg.senderId) !== String(effectiveUserId)
          ) {
            return msg;
          }
          return {
            ...msg,
            status: payload.status || "read",
            deliveredAt: payload.deliveredAt || msg.deliveredAt,
            readAt: payload.readAt || msg.readAt,
          };
        }),
      );
    };

    socket.on("chat:typing", handleTyping);
    socket.on("chat:delivered", handleDelivered);
    socket.on("chat:message", handleMessage);
    socket.on("chat:read", handleRead);

    return () => {
      const s = getSocket();
      if (s) {
        s.off("chat:typing", handleTyping);
        s.off("chat:delivered", handleDelivered);
        s.off("chat:message", handleMessage);
        s.off("chat:read", handleRead);
      }
    };
  }, [accessToken, conversationId, effectiveUserId]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !conversationId) return;

    if (!draft.trim()) {
      socket.emit("chat:typing", { roomId: conversationId, isTyping: false });
      return;
    }

    socket.emit("chat:typing", { roomId: conversationId, isTyping: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.emit("chat:typing", { roomId: conversationId, isTyping: false });
    }, 1200);

    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [draft, conversationId]);

  useEffect(() => {
    const socket = getSocket();
    if (!socket || !conversationId || !effectiveUserId) return;

    messages.forEach((message) => {
      const messageId = message.id || message._id;
      if (!messageId) return;
      const messageKey = String(messageId);
      const isIncoming = String(message.senderId) !== String(effectiveUserId);
      if (!isIncoming) return;

      if (!deliveredRef.current.has(messageKey)) {
        socket.emit("chat:delivered", { messageId: messageKey });
        deliveredRef.current.add(messageKey);
      }
      if (!readRef.current.has(messageKey)) {
        socket.emit("chat:read", { messageId: messageKey });
        readRef.current.add(messageKey);
      }
    });
  }, [messages, conversationId, effectiveUserId]);

  const refreshLatestMessages = useCallback(async () => {
    if (!conversationId) return;
    try {
      const res = await api.get(`/v1/chat/messages/${conversationId}`, {
        params: { limit: PAGE_SIZE },
      });
      const latest = res.data?.messages || [];
      if (!latest.length) return;

      setMessages((prev) => {
        const latestById = new Map(
          latest
            .map((msg) => {
              const id = msg.id || msg._id;
              return id ? [String(id), msg] : null;
            })
            .filter(Boolean),
        );

        const prevIds = new Set(
          prev
            .map((msg) => msg.id || msg._id)
            .filter(Boolean)
            .map((id) => String(id)),
        );

        const merged = prev.map((msg) => {
          const id = msg.id || msg._id;
          if (!id) return msg;
          return latestById.get(String(id)) || msg;
        });

        const newOnes = latest.filter((msg) => {
          const id = msg.id || msg._id;
          return id && !prevIds.has(String(id));
        });

        return newOnes.length ? [...merged, ...newOnes] : merged;
      });
    } catch (err) {
      console.error(err);
    }
  }, [conversationId]);

  const handleManualRefresh = useCallback(async () => {
    if (!conversationId || isRefreshing) return;
    setIsRefreshing(true);
    const startedAt = Date.now();
    try {
      await refreshLatestMessages();
      scrollToBottomSoon();
    } finally {
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(300, 800 - elapsed);
      window.setTimeout(() => setIsRefreshing(false), remaining);
    }
  }, [conversationId, isRefreshing, refreshLatestMessages, scrollToBottomSoon]);

  useEffect(() => {
    if (!hasPendingMedia) {
      if (mediaRefreshRef.current.timeoutId) {
        clearInterval(mediaRefreshRef.current.timeoutId);
      }
      mediaRefreshRef.current = { timeoutId: null, attempts: 0 };
      return;
    }

    if (mediaRefreshRef.current.timeoutId) return;
    mediaRefreshRef.current.attempts = 0;
    mediaRefreshRef.current.timeoutId = setInterval(() => {
      mediaRefreshRef.current.attempts += 1;
      if (mediaRefreshRef.current.attempts > MEDIA_REFRESH_MAX_RETRIES) {
        clearInterval(mediaRefreshRef.current.timeoutId);
        mediaRefreshRef.current.timeoutId = null;
        return;
      }
      refreshLatestMessages();
    }, MEDIA_REFRESH_DELAY_MS);

    return () => {
      if (mediaRefreshRef.current.timeoutId) {
        clearInterval(mediaRefreshRef.current.timeoutId);
        mediaRefreshRef.current.timeoutId = null;
      }
    };
  }, [hasPendingMedia, refreshLatestMessages]);

  useEffect(() => {
    if (loadingMore) return;
    if (isAtBottomRef.current) {
      requestAnimationFrame(() => {
        scrollToBottom();
      });
    }
  }, [messages, loadingMore, scrollToBottom]);

  const getStatusLabel = useCallback((message) => {
    const status = message?.status || "sent";
    if (status === "read") return "Read";
    if (status === "delivered") return "Delivered";
    return "Sent";
  }, []);

  const handlePickAttachment = () => {
    if (uploading) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = useCallback(
    async (event) => {
      if (!conversationId) return;
      const file = event.target.files?.[0];
      event.target.value = "";
      if (!file) return;

      setUploadError("");
      setUploading(true);

      try {
        if (IMAGE_TYPES.includes(file.type)) {
          const ext = extFromFile(file) || "jpg";
          const { data: presign } = await api.post(
            "/v1/chat/media/presign-image-poster",
            { ext, contentType: file.type || "image/jpeg" },
          );

          await putToS3(presign.uploadUrl, file, file.type);

          const attachment = buildImageAttachment(file, presign);
          if (!attachment) throw new Error("Image upload failed");

          setPendingAttachment({
            type: "image",
            previewUrl: URL.createObjectURL(file),
            attachment,
            filename: file.name,
          });
          return;
        }

        if (VIDEO_TYPES.includes(file.type)) {
          const filename = safeStr(file?.name) || "video.mp4";
          const clientMeta = (await sniffVideoMeta(file).catch(() => null)) || {
            width: 0,
            height: 0,
            orientation: "landscape",
          };

          const { data: presign } = await api.post(
            "/v1/chat/media/presign-video-source",
            { filename, contentType: file.type || "video/mp4", clientMeta },
          );

          await putToS3(presign.uploadUrl, file, presign.contentType);
          await api.post(`/v1/videos/${presign.videoId}/submit`);

          const attachment = buildVideoAttachment(file, presign);
          if (!attachment) throw new Error("Video upload failed");

          setPendingAttachment({
            type: "video",
            previewUrl: URL.createObjectURL(file),
            attachment,
            filename: file.name,
          });
          return;
        }

        setUploadError(
          "Unsupported file type. Please upload an image or video.",
        );
      } catch (err) {
        console.error(err);
        setUploadError(
          err?.response?.data?.message ||
            err?.message ||
            "Unable to upload attachment.",
        );
      } finally {
        setUploading(false);
      }
    },
    [conversationId],
  );

  const clearPendingAttachment = () => {
    if (pendingAttachment?.previewUrl) {
      URL.revokeObjectURL(pendingAttachment.previewUrl);
    }
    setPendingAttachment(null);
  };

  const handleSend = useCallback(async () => {
    const trimmed = draft.trim();
    if (!trimmed && !pendingAttachment) return;
    if (!conversationId || sending || uploading) return;

    try {
      setSending(true);
      const res = await api.post("/v1/chat/messages", {
        conversationId,
        text: hasText(trimmed) ? trimmed : null,
        attachments: pendingAttachment
          ? [pendingAttachment.attachment]
          : undefined,
      });

      const newMessage = res.data?.message;
      if (newMessage) {
        const newMessageId = newMessage.id || newMessage._id;
        setMessages((prev) => {
          if (!newMessageId) return [...prev, newMessage];
          if (
            prev.some(
              (msg) => String(msg.id || msg._id) === String(newMessageId),
            )
          ) {
            return prev;
          }
          return [...prev, newMessage];
        });
      }

      setDraft("");
      setPendingAttachment(null);

      // ✅ keep layout stable after send while keyboard is open
      scrollToBottomSoon();
    } catch (err) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          "Unable to send message. Please try again.",
      );
    } finally {
      setSending(false);
    }
  }, [
    draft,
    conversationId,
    pendingAttachment,
    sending,
    uploading,
    scrollToBottomSoon,
  ]);

  const handleLoadMore = useCallback(async () => {
    if (!conversationId || loadingMore || !hasMore) return;
    const oldest = messages[0];
    const before = oldest?.createdAt || oldest?.sentAt;
    if (!before) return;

    const pane = messagePaneRef.current;
    const prevScrollHeight = pane?.scrollHeight || 0;
    const prevScrollTop = pane?.scrollTop || 0;

    try {
      setLoadingMore(true);
      const res = await api.get(`/v1/chat/messages/${conversationId}`, {
        params: { limit: PAGE_SIZE, before },
      });
      const newMessages = res.data?.messages || [];
      if (newMessages.length) {
        setMessages((prev) => [...newMessages, ...prev]);
      }
      setHasMore(Boolean(res.data?.hasMore));
    } catch (err) {
      console.error(err);
    } finally {
      requestAnimationFrame(() => {
        if (!pane) return;
        const nextScrollHeight = pane.scrollHeight;
        pane.scrollTop = nextScrollHeight - prevScrollHeight + prevScrollTop;
      });
      setLoadingMore(false);
    }
  }, [conversationId, hasMore, loadingMore, messages]);

  const handleMessagePaneScroll = useCallback(
    (event) => {
      const pane = event.currentTarget;
      const distanceToBottom =
        pane.scrollHeight - pane.scrollTop - pane.clientHeight;
      isAtBottomRef.current = distanceToBottom < LOAD_MORE_THRESHOLD;
      if (pane.scrollTop <= LOAD_MORE_THRESHOLD) {
        handleLoadMore();
      }
    },
    [handleLoadMore],
  );

  const renderAttachments = (message) => {
    const attachments = Array.isArray(message?.attachments)
      ? message.attachments
      : [];
    if (!attachments.length) return null;

    return (
      <div className={cls.attachmentGrid}>
        {attachments.map((att, idx) => {
          if (att.type === "image") {
            const url = buildImagePreview(att);
            if (!url) {
              return (
                <div key={`${att.key}-${idx}`} className={cls.mediaLoading}>
                  Processing photo... Please wait.
                </div>
              );
            }
            return (
              <img
                key={`${att.key}-${idx}`}
                src={url}
                alt="Chat attachment"
                className={cls.attachmentImage}
                loading="lazy"
              />
            );
          }

          if (att.type === "video") {
            const meta = buildVideoMeta(att);
            if (!meta?.videoId && !meta?.hlsUrl) {
              return (
                <div key={`${att.key}-${idx}`} className={cls.mediaLoading}>
                  Processing video... Please wait.
                </div>
              );
            }

            const canProxyPlay = Boolean(meta.videoId && accessToken);
            const canDirectPlay = Boolean(meta.hlsUrl);

            if (canProxyPlay) {
              return (
                <VideoPlayer
                  key={`${att.key}-${idx}`}
                  token={accessToken}
                  videoId={meta.videoId}
                  mode="proxy"
                  poster={meta.posterUrl || undefined}
                  autoPlay={false}
                  muted
                  controls
                  className={cls.attachmentVideo}
                />
              );
            }

            if (canDirectPlay) {
              return (
                <video
                  key={`${att.key}-${idx}`}
                  className={cls.attachmentVideo}
                  src={meta.hlsUrl}
                  poster={meta.posterUrl || undefined}
                  controls
                  preload="metadata"
                  playsInline
                  controlsList="nodownload"
                />
              );
            }

            return (
              <div key={`${att.key}-${idx}`} className={cls.mediaLoading}>
                Processing video... Please wait.
              </div>
            );
          }

          return null;
        })}
      </div>
    );
  };

  return (
    <div className={cls.page}>
      <div className={cls.pageInner}>
        <div className={cls.chatShell}>
          <header className={cls.chatHeader}>
            <Link
              className={cls.roundBtn}
              to={memberStatusPath}
              aria-label={i18n.t("homeLayout.home")}
            >
              <QuickIcon
                src={homeIcon}
                alt={i18n.t("homeLayout.home")}
                ring="pink"
                active={pathname === memberStatusPath}
              />
            </Link>

            <div className={cls.headerMain}>
              <div className={cls.headerProfile}>
                <div className={cls.headerAvatar}>
                  <img src={headerAvatar} alt={headerName} />
                  <span className={cls.headerOnline} />
                </div>
                <div>
                  <h1 className={cls.headerName}>{headerName}</h1>
                  <p className={cls.headerStatus}>
                    {isTyping
                      ? "Typing..."
                      : "Admin team responds within 24 hours."}
                  </p>
                </div>
              </div>

              <button
                type="button"
                className={`${cls.refreshButton} ${
                  isRefreshing ? cls.refreshButtonActive : ""
                }`}
                onClick={handleManualRefresh}
                disabled={loading || !conversationId || isRefreshing}
                title="Refresh media and messages"
              >
                <span className={cls.refreshIcon} aria-hidden="true">
                  ⟳
                </span>
                <span className={cls.refreshLabel}>
                  {isRefreshing ? "Refreshing..." : "Refresh"}
                </span>
              </button>
            </div>
          </header>

          <div
            className={cls.messagePane}
            ref={messagePaneRef}
            onScroll={handleMessagePaneScroll}
          >
            {loadingMore && (
              <p className={cls.loadMoreNotice}>Loading older messages...</p>
            )}
            {loading && <p>Loading conversation...</p>}
            {!loading && error && <p>{error}</p>}
            {!loading && !error && messages.length === 0 && (
              <p>No messages yet. Say hello to start the chat.</p>
            )}

            {!loading &&
              !error &&
              messages.map((message, index) => {
                const isOutgoing =
                  String(message.senderId) === String(effectiveUserId);

                const createdAt = message.createdAt || message.sentAt;

                const previous = messages[index - 1];
                const previousDate = previous?.createdAt || previous?.sentAt;

                const showDivider =
                  index === 0 ||
                  formatDateLabel(previousDate) !== formatDateLabel(createdAt);

                const attachments = Array.isArray(message.attachments)
                  ? message.attachments
                  : [];
                const hasAttachments = attachments.length > 0;

                const textValue = hasText(message.text) ? message.text : "";
                const isMediaOnly = hasAttachments && !hasText(message.text);

                return (
                  <React.Fragment key={message.id || message._id || index}>
                    {showDivider && (
                      <div className={cls.dateDivider}>
                        {formatDateLabel(createdAt)}
                      </div>
                    )}

                    <div
                      className={`${cls.messageRow} ${
                        isOutgoing
                          ? cls.messageRowOutgoing
                          : cls.messageRowIncoming
                      }`}
                    >
                      {!isOutgoing && (
                        <img
                          src={headerAvatar}
                          alt={headerName}
                          className={cls.messageAvatar}
                        />
                      )}

                      <div
                        className={`${cls.bubble} ${
                          isOutgoing ? cls.bubbleOutgoing : cls.bubbleIncoming
                        } ${isMediaOnly ? cls.bubbleMediaOnly : ""}`}
                      >
                        <div className={cls.bubbleBody}>
                          {renderAttachments(message)}
                          {textValue && (
                            <p className={cls.bubbleText}>{textValue}</p>
                          )}
                        </div>

                        {/* ✅ WhatsApp-like: meta row UNDER the content */}
                        <div
                          className={`${cls.bubbleMeta} ${
                            isOutgoing
                              ? cls.bubbleMetaOutgoing
                              : cls.bubbleMetaIncoming
                          }`}
                        >
                          {isOutgoing && (
                            <span className={cls.bubbleStatus}>
                              {getStatusLabel(message)}
                            </span>
                          )}
                          <span className={cls.bubbleTime}>
                            {formatTime(createdAt)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}

            {!loading && !error && isTyping && (
              <div className={cls.typingRow}>
                <div className={cls.typingBubble}>
                  <span />
                  <span />
                  <span />
                </div>
              </div>
            )}
          </div>

          {pendingAttachment && (
            <div className={cls.pendingAttachment}>
              <div className={cls.pendingPreview}>
                {pendingAttachment.type === "image" ? (
                  <img
                    src={pendingAttachment.previewUrl}
                    alt="Pending upload"
                  />
                ) : (
                  <video src={pendingAttachment.previewUrl} />
                )}
              </div>
              <div className={cls.pendingMeta}>
                <p>{pendingAttachment.filename || "Attachment"}</p>
                <button
                  type="button"
                  className={cls.pendingRemove}
                  onClick={clearPendingAttachment}
                >
                  Remove
                </button>
              </div>
            </div>
          )}

          <footer className={cls.composer}>
            <input
              ref={fileInputRef}
              type="file"
              accept={[...IMAGE_TYPES, ...VIDEO_TYPES].join(",")}
              onChange={handleFileChange}
              className={cls.fileInput}
            />

            <button
              type="button"
              className={cls.composerAction}
              onClick={handlePickAttachment}
              disabled={uploading}
            >
              +
            </button>

            <div className={cls.composerInputWrap}>
              <textarea
                ref={inputRef}
                placeholder="Write a message..."
                className={cls.composerInput}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (
                    event.key === "Enter" &&
                    (event.ctrlKey || event.metaKey) &&
                    !event.shiftKey
                  ) {
                    event.preventDefault();
                    handleSend();
                  }
                }}
                disabled={sending || uploading}
                rows={3}
                inputMode="text"
                autoCapitalize="sentences"
                autoCorrect="on"
              />
            </div>

            <button
              type="button"
              className={cls.sendButton}
              onClick={handleSend}
              disabled={
                sending || uploading || (!draft.trim() && !pendingAttachment)
              }
            >
              {uploading ? "Uploading..." : "Send"}
            </button>
          </footer>

          {uploadError && <p className={cls.uploadError}>{uploadError}</p>}
        </div>
      </div>
    </div>
  );
};

export default Chat;
